/**
 * FeatureGrantsPanel: the admin comp switch for one user (per-user grants in
 * user_entitlements). Lists the user's grants with their end dates, grants a
 * feature with an optional end date, and revokes one. PRP-MCP-7 (F9) added
 * `mcp`: turn chat on for one person with no subscription.
 */
import { useCallback, useEffect, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { AdminFeatureGrant } from '../../lib/api';

/** Grantable features, matching VALID_FEATURES in app/services/entitlements/service.py. */
export const GRANT_FEATURES: { id: string; label: string }[] = [
  { id: 'mcp', label: 'Chat (Claude / ChatGPT)' },
  { id: 'sketch', label: 'Sketch' },
  { id: 'scope_gen', label: 'Scope generation' },
  { id: 'enhance', label: 'Enhance' },
  { id: 'convert', label: 'Convert' },
];

function featureLabel(id: string): string {
  return GRANT_FEATURES.find((f) => f.id === id)?.label ?? id;
}

function isExpired(iso: string | null): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= Date.now();
}

export default function FeatureGrantsPanel({ userId }: { userId: string }) {
  const [grants, setGrants] = useState<AdminFeatureGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feature, setFeature] = useState('mcp');
  const [days, setDays] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    BackendAPI.adminListUserGrants(userId)
      .then((res) => setGrants(res.grants ?? []))
      .catch((e) => setError(e?.message || 'Failed to load grants'));
  }, [userId]);

  useEffect(load, [load]);

  const grant = async () => {
    const n = days.trim() ? Number(days) : null;
    if (n !== null && (!Number.isInteger(n) || n < 1)) {
      setError('End after must be a whole number of days, or empty for no end date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await BackendAPI.adminGrantUserFeature(userId, feature, n);
      setGrants(res.grants ?? []);
      setDays('');
    } catch (e: any) {
      setError(e?.message || 'Grant failed');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (f: string) => {
    setBusy(true);
    setError(null);
    try {
      await BackendAPI.adminRevokeUserFeature(userId, f);
      load();
    } catch (e: any) {
      setError(e?.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 text-xs" data-testid="feature-grants">
      {error && <p className="text-red-700 dark:text-red-300">{error}</p>}
      {!error && grants === null && <p className="text-muted-foreground">Loading…</p>}
      {grants && grants.length === 0 && (
        <p className="text-muted-foreground">No features granted.</p>
      )}
      {grants && grants.length > 0 && (
        <ul className="space-y-1" aria-label="Feature grants">
          {grants.map((g) => (
            <li key={g.feature} className="flex items-center justify-between gap-2">
              <span>
                {featureLabel(g.feature)}
                <span className="ml-2 text-muted-foreground">
                  {g.expires_at
                    ? `${isExpired(g.expires_at) ? 'ended' : 'until'} ${g.expires_at.slice(0, 10)}`
                    : 'no end date'}
                </span>
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void revoke(g.feature)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted"
                aria-label={`Revoke ${featureLabel(g.feature)}`}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <select
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          aria-label="Feature to grant"
          disabled={busy}
          className="rounded border border-border bg-background px-2 py-1"
        >
          {GRANT_FEATURES.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          value={days}
          onChange={(e) => setDays(e.target.value)}
          placeholder="Days (empty = no end)"
          aria-label="End after days"
          inputMode="numeric"
          disabled={busy}
          className="w-36 rounded border border-border bg-background px-2 py-1"
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => void grant()}
          className="rounded bg-primary px-3 py-1 text-primary-foreground hover:opacity-90"
        >
          Grant
        </button>
      </div>
    </div>
  );
}
